import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Box from "@material-ui/core/Box";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_SimpleSelect from "../../../Atoms/Inputs/Selects/A_SimpleSelect";
import A_Switch from "../../../Atoms/Inputs/Switches/A_Switch";
import IconAction from "./IconAction";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import {
  api,
  fetchBankOptionSets,
  BANK_DETAILS_SOURCE,
  DEFAULT_LOCKED_BANK_FIELDS,
} from "./ProfessionalContacts.helper";
import {
  isValidSortCode,
  SORT_CODE_FORMAT_ERROR,
} from "../../../../../domain/professionalContacts/professionalContacts.rules";
import useStyles from "./ProfessionalContacts.styles";

const SORT_CODE_FETCHING_MESSAGE = "Fetching bank details, please wait...";
const SORT_CODE_MIN_LENGTH_FOR_ERROR = 8;
const ACCOUNT_NUMBER_MAX_LENGTH = 10;

const BLANK_LOOKUP = {
  loading: false,
  mode: "manual",
  readOnly: false,
  error: "",
  lockedFields: [],
};

const LOOKUP_FILLED_FIELDS = ["bankName", "town", "postcode"];

const BankAccountRowsEditor = ({ bankAccounts, onChange }) => {
  const classes = useStyles();
  const [bankTypeOptions, setBankTypeOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [lookupByRow, setLookupByRow] = useState({});
  const [sortCodeTouchedByRow, setSortCodeTouchedByRow] = useState({});
  const lastLookedUpByRow = useRef({});
  const seededLockByRow = useRef({});
  const rowsRef = useRef(bankAccounts);
  rowsRef.current = bankAccounts;

  useEffect(() => {
    fetchBankOptionSets().then(({ bankType, country }) => {
      setBankTypeOptions(bankType);
      setCountryOptions(country);
    });
  }, []);

  useEffect(() => {
    bankAccounts.forEach((row) => {
      if (row.sortCode && lastLookedUpByRow.current[row._key] === undefined) {
        lastLookedUpByRow.current[row._key] = row.sortCode;
      }
    });
  }, [bankAccounts]);

  // A saved row arrives from GET /professional-firms/{firmId} with the creditor bank fields the
  // API has locked. Seed the row's lookup state from that so those fields open read-only and stay
  // that way until the user edits the sort code, which is what re-runs the lookup.
  useEffect(() => {
    const seeded = {};
    bankAccounts.forEach((row) => {
      if (!row.bankDetailsLocked || seededLockByRow.current[row._key]) return;
      seededLockByRow.current[row._key] = true;
      seeded[row._key] = {
        ...BLANK_LOOKUP,
        mode: "api",
        readOnly: true,
        lockedFields: row.lockedFields?.length
          ? row.lockedFields
          : DEFAULT_LOCKED_BANK_FIELDS,
      };
    });
    if (Object.keys(seeded).length) {
      setLookupByRow((prev) => ({ ...prev, ...seeded }));
    }
  }, [bankAccounts]);

  const renderLockedLabel = (text) => (
    <Box component="span" className={classes.lockedLabel}>
      {text}
      <LockOutlinedIcon className={classes.lockedLabelIcon} />
    </Box>
  );

  const updateRow = (rowKey, field, value) => {
    onChange(
      bankAccounts.map((row) =>
        row._key === rowKey ? { ...row, [field]: value } : row
      )
    );
  };

  const setMain = (rowKey) => {
    onChange(
      bankAccounts.map((row) => ({ ...row, isMain: row._key === rowKey }))
    );
  };

  const addRow = () => {
    onChange([
      ...bankAccounts,
      {
        _key: `new-${Date.now()}-${bankAccounts.length}`,
        beneficiaryName: "",
        bankType: "",
        accountNumber: "",
        sortCode: "",
        bankName: "",
        town: "",
        postcode: "",
        country: "",
        address: "",
        isMain: bankAccounts.length === 0,
      },
    ]);
  };

  const removeRow = (rowKey) => {
    const wasMain = bankAccounts.find((r) => r._key === rowKey)?.isMain;
    const next = bankAccounts.filter((row) => row._key !== rowKey);
    if (wasMain && next.length && !next.some((r) => r.isMain))
      next[0].isMain = true;
    onChange(next);
    setLookupByRow((prev) => {
      // eslint-disable-next-line no-unused-vars
      const { [rowKey]: _removed, ...rest } = prev;
      return rest;
    });
    setSortCodeTouchedByRow((prev) => {
      // eslint-disable-next-line no-unused-vars
      const { [rowKey]: _removed, ...rest } = prev;
      return rest;
    });
  };

  // Clearing the looked-up fields and recording the Manual source must happen in ONE onChange:
  // rowsRef only catches up when the parent re-renders, so two calls in the same tick would both
  // map the pre-update rows and the second would silently restore what the first cleared.
  const resetRowToManual = (rowKey, clearBankFields) => {
    onChange(
      rowsRef.current.map((row) =>
        row._key === rowKey
          ? {
              ...row,
              ...(clearBankFields
                ? { bankName: "", town: "", postcode: "", country: "" }
                : {}),
              bankDetailsSource: BANK_DETAILS_SOURCE.MANUAL,
            }
          : row
      )
    );
  };

  const handleAccountNumberChange = (rowKey, rawValue) => {
    const digitsOnly = rawValue
      .replace(/\D/g, "")
      .slice(0, ACCOUNT_NUMBER_MAX_LENGTH);
    updateRow(rowKey, "accountNumber", digitsOnly);
  };

  const handleSortCodeChange = async (rowKey, rawValue) => {
    updateRow(rowKey, "sortCode", rawValue);
    setSortCodeTouchedByRow((prev) =>
      prev[rowKey] ? { ...prev, [rowKey]: false } : prev
    );

    if (!rawValue) {
      lastLookedUpByRow.current[rowKey] = "";
      return;
    }
    if (!isValidSortCode(rawValue)) {
      return;
    }
    if (lastLookedUpByRow.current[rowKey] === rawValue) return;
    lastLookedUpByRow.current[rowKey] = rawValue;

    const wasReadOnly = Boolean(lookupByRow[rowKey]?.readOnly);
    setLookupByRow((prev) => ({
      ...prev,
      [rowKey]: { ...BLANK_LOOKUP, loading: true },
    }));
    try {
      const result = await api.lookupSortCode(rawValue.replace(/-/g, ""));
      if (result?.found) {
        const hasCountry = Boolean(result.country);
        onChange(
          rowsRef.current.map((row) =>
            row._key === rowKey
              ? {
                  ...row,
                  bankName: result.bankName || row.bankName,
                  town: result.town || row.town,
                  postcode: result.postcode || row.postcode,
                  country: hasCountry ? result.country : row.country,
                  bankDetailsSource: BANK_DETAILS_SOURCE.LOOKUP,
                }
              : row
          )
        );
        setLookupByRow((prev) => ({
          ...prev,
          [rowKey]: {
            loading: false,
            mode: hasCountry ? "api" : "manual",
            readOnly: true,
            error: "",
            lockedFields: hasCountry
              ? [...LOOKUP_FILLED_FIELDS, "country"]
              : LOOKUP_FILLED_FIELDS,
          },
        }));
      } else {
        resetRowToManual(rowKey, wasReadOnly);
        setLookupByRow((prev) => ({
          ...prev,
          [rowKey]: {
            ...BLANK_LOOKUP,
            error: result?.error || "No bank found for this sort code",
          },
        }));
      }
    } catch (err) {
      resetRowToManual(rowKey, wasReadOnly);
      setLookupByRow((prev) => ({
        ...prev,
        [rowKey]: {
          ...BLANK_LOOKUP,
          error:
            err?.message || "Failed to look up bank details for this sort code",
        },
      }));
    }
  };

  const selectProps = { size: "small", style: { margin: 0, width: "100%" } };

  return (
    <div>
      <div className={classes.sectionHeading}>
        Bank details
        <button
          type="button"
          className={clsx(classes.addRowButton, classes.headingAction)}
          onClick={addRow}
        >
          <AddIcon fontSize="inherit" />{" "}
          {bankAccounts.length === 0
            ? "Add bank account"
            : "Add another bank account"}
        </button>
      </div>
      <M_DataStateRenderer
        loading={false}
        error={null}
        data={bankAccounts}
        isEmpty={bankAccounts.length === 0}
        emptyMessage="No bank details available."
      >
        {null}
      </M_DataStateRenderer>
      {bankAccounts.map((row, index) => {
        const lookup = lookupByRow[row._key] || BLANK_LOOKUP;
        // Only the fields the lookup filled (or the API named in lockedFields) are locked; the
        // rest of the row stays editable. Everything locks while a lookup is in flight.
        const isLocked = (field) =>
          lookup.loading ||
          (lookup.readOnly && lookup.lockedFields.includes(field));
        const countryLocked = isLocked("country");
        const sortCodeFormatError =
          row.sortCode &&
          !isValidSortCode(row.sortCode) &&
          (sortCodeTouchedByRow[row._key] ||
            row.sortCode.length >= SORT_CODE_MIN_LENGTH_FOR_ERROR)
            ? SORT_CODE_FORMAT_ERROR
            : "";
        return (
          <div key={row._key} className={classes.editorRow}>
            <div className={classes.editorRowHead}>
              <span className={classes.editorRowLabel}>
                Bank account {index + 1}
              </span>
              <div className={classes.editorRowActions}>
                <A_Switch
                  label="Main"
                  labelPlacement="end"
                  size="small"
                  checked={Boolean(row.isMain)}
                  onChange={() => setMain(row._key)}
                />
                <IconAction
                  danger
                  title="Remove Bank Account"
                  icon={<DeleteIcon fontSize="small" />}
                  onClick={() => removeRow(row._key)}
                />
              </div>
            </div>
            <div className={classes.editorGrid}>
              <A_TextField
                id={`bank-beneficiaryName-${row._key}`}
                label="Beneficiary Name *"
                className={classes.field}
                defaultValue={row.beneficiaryName}
                onChange={(e) =>
                  updateRow(row._key, "beneficiaryName", e.target.value)
                }
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
              />
              <A_SimpleSelect
                id={`bank-bankType-${row._key}`}
                label="Account Type *"
                className={classes.field}
                defaultValue={row.bankType || ""}
                options={bankTypeOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(value) => updateRow(row._key, "bankType", value)}
                {...selectProps}
              />
              <A_TextField
                id={`bank-accountNumber-${row._key}`}
                label="Account Number *"
                className={classes.field}
                defaultValue={row.accountNumber}
                inputType="value"
                onChange={(e) =>
                  handleAccountNumberChange(row._key, e.target.value)
                }
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
              />
              <A_TextField
                id={`bank-sortCode-${row._key}`}
                label="Sort Code *"
                inputProps={{ placeholder: "12-34-56" }}
                className={classes.field}
                defaultValue={row.sortCode}
                onChange={(e) => handleSortCodeChange(row._key, e.target.value)}
                onBlur={() =>
                  setSortCodeTouchedByRow((prev) => ({
                    ...prev,
                    [row._key]: true,
                  }))
                }
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
                error={Boolean(
                  !lookup.loading && (sortCodeFormatError || lookup.error)
                )}
                helperText={
                  lookup.loading
                    ? SORT_CODE_FETCHING_MESSAGE
                    : sortCodeFormatError || lookup.error
                }
              />
              <A_TextField
                id={`bank-bankName-${row._key}`}
                label={
                  isLocked("bankName")
                    ? renderLockedLabel("Creditor Bank Name *")
                    : "Creditor Bank Name *"
                }
                className={classes.field}
                defaultValue={row.bankName}
                inputType="value"
                onChange={(e) =>
                  updateRow(row._key, "bankName", e.target.value)
                }
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
                disabled={isLocked("bankName")}
              />
              <A_TextField
                id={`bank-town-${row._key}`}
                label={
                  isLocked("town")
                    ? renderLockedLabel("Creditor Bank Town *")
                    : "Creditor Bank Town *"
                }
                className={classes.field}
                defaultValue={row.town}
                inputType="value"
                onChange={(e) => updateRow(row._key, "town", e.target.value)}
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
                disabled={isLocked("town")}
              />
              <A_TextField
                id={`bank-postcode-${row._key}`}
                label={
                  isLocked("postcode")
                    ? renderLockedLabel("Creditor Bank Postcode *")
                    : "Creditor Bank Postcode *"
                }
                className={classes.field}
                defaultValue={row.postcode}
                inputType="value"
                onChange={(e) =>
                  updateRow(row._key, "postcode", e.target.value)
                }
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
                disabled={isLocked("postcode")}
              />
              {countryLocked ? (
                <A_TextField
                  id={`bank-country-${row._key}`}
                  label={renderLockedLabel("Creditor Bank Country *")}
                  className={classes.field}
                  defaultValue={row.country}
                  inputType="value"
                  onChange={(e) =>
                    updateRow(row._key, "country", e.target.value)
                  }
                  fullWidth
                  size="small"
                  margin="none"
                  variant="outlined"
                  disabled
                />
              ) : (
                <A_SimpleSelect
                  id={`bank-country-${row._key}`}
                  label="Creditor Bank Country *"
                  className={classes.field}
                  defaultValue={row.country || ""}
                  options={countryOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(value) => updateRow(row._key, "country", value)}
                  {...selectProps}
                />
              )}
              <A_TextField
                id={`bank-address-${row._key}`}
                label="Creditor Bank Address *"
                className={classes.field}
                defaultValue={row.address}
                onChange={(e) => updateRow(row._key, "address", e.target.value)}
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

BankAccountRowsEditor.propTypes = {
  bankAccounts: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default BankAccountRowsEditor;
